<template>
  <div ref="filterItemRef" class="filter-item-wrapper">
    <Chip
      class="chip whitespace-nowrap"
      :class="{ 'chip--open': isOpen && !isToggle }"
      :selected="isChipSelected"
      :classes="classes"
      :selected-classes="selectedClasses"
      @click="onClickChip"
    >
      <template #prefix>
        <slot name="prefix" :is-selected="isChipSelected" :is-open="isOpen" />
      </template>
      {{ getLabel?.() || chipLabel || label }}
      <div v-if="!isToggle" class="count">
        {{ selectedOptionsCount || '' }}
      </div>
      <template #suffix>
        <slot name="suffix" />
      </template>
    </Chip>

    <transition name="fade" mode="out-in">
      <div
        v-if="isOpen && !isToggle"
        class="filter-item"
        :class="'filter-item--' + filterKey"
      >
        <div class="filter-item__content">
          <div v-if="showSearch" class="search">
            <Search
              class="search__leading-icon"
              :size="18"
              aria-hidden="true"
            />
            <input
              ref="searchInputRef"
              v-model="filterOptionsSearchTerm"
              type="text"
              inputmode="search"
              enterkeyhint="search"
              :placeholder="placeholder"
              class="search-input"
              @input="handleSearchInput"
            />
            <button
              v-if="filterOptionsSearchTerm"
              type="button"
              class="search__clear"
              :aria-label="t('search.selectLocalityComponent.clearSearchAria')"
              @click="clearFilterSearch"
            >
              <X :size="24" stroke-width="2" aria-hidden="true" />
            </button>
          </div>

          <div
            class="filter-item__options"
            :class="{
              'filter-item__options--pill':
                filterKey === 'location' || filterKey === 'categories',
            }"
          >
            <slot
              :model-value="temporarySelectedOptions"
              name="content"
              :updated="selectTempOptForSlot"
              :confirmed="confirmSelect"
            />

            <div v-for="opt in options" :key="opt.slug">
              <FilterOption
                :selected="isLocalityOptionSelected(opt.slug)"
                :label="opt.name"
                :count="opt.count"
                :show-location-icons="filterKey === 'location'"
                :is-region="isLocalityRegion(opt)"
                @click="selectTemporaryOption(opt.slug)"
              />
            </div>
          </div>

          <div
            class="buttons"
            :class="{
              'buttons--hide': isButtonHide,
            }"
          >
            <!-- prettier-ignore-attribute -->
            <Button
              class="button button--cancel"
              variant="text"
              @click="closeFilter"
            >
              {{ btnLabelCancel || $t('search.buttons.cancel') }}
            </Button>
            <!-- prettier-ignore-attribute -->
            <Button
              ref="completeButtonRef"
              class="button button--confirm"
              @click="confirmSelect"
            >
              <span class="hidden md:block">
                {{ btnLabelConfirm || $t('search.buttons.confirm') }}
              </span>
              <span class="block md:hidden">
                {{ btnLabelConfirm || $t('search.buttons.confirmMobile') }}
              </span>
            </Button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup lang="ts">
import nth from 'lodash/nth'
import { nextTick } from 'vue'
import { onClickOutside } from '@vueuse/core'
import type { SearchResponse } from '@nuxtjs/algolia'
import { localitySlugMatchesFilter } from '~/utils/localitySlugMatch'
import type { SearchEngineLocation } from '~/composables/types/searchEngine'
import { X, Search } from 'lucide-vue-next'
import Chip from '~/components/common/Chip.vue'
import { sendFilterOpen } from '~/composables/useGtmEvents'

const { isMobile } = useDevice()
const { t } = useI18n()

const emit = defineEmits(['filtersUpdated', 'searchUpdated'])
type filterOption = {
  name: string
  slug: string
  count?: number
}
const props = withDefaults(
  defineProps<{
    isSlide?: boolean
    iconActive?: any
    label: string
    filterKey: string
    multiSelect?: boolean
    classes?: string
    selectedClasses?: string
    options?: string[] | filterOption[]
    placeholder?: string
    showSearch?: boolean
    isToggle?: boolean
    selectedOptions?: string[] | filterOption
    getLabel?: () => string
    dialogTitle?: string
    btnLabelClear?: string
    btnLabelConfirm?: string
    btnLabelCancel?: string
    isButtonHide?: boolean
    mobileLabel?: string
  }>(),
  {
    isSlide: false,
    iconActive: undefined,
    options: () => [],
    placeholder: 'search',
    showSearch: false,
    classes: '',
    selectedClasses: '',
    dialogTitle: '',
    isToggle: false,
    selectedOptions: () => [],
    multiSelect: false,
    getLabel: undefined,
    btnLabelClear: undefined,
    btnLabelConfirm: undefined,
    btnLabelCancel: undefined,
    isButtonHide: false,
    mobileLabel: undefined,
  }
)
const completeButtonRef = ref(null)
const searchInputRef = ref<HTMLInputElement | null>(null)

const isOpen = ref(false)

const filterOptionsSearchTerm = ref('')
let temporarySelectedOptions = resetTempSelectedOptions()

function handleSearchInput(event: Event) {
  const target = event.target as HTMLInputElement
  emit('searchUpdated', target.value)
}

function clearFilterSearch() {
  filterOptionsSearchTerm.value = ''
  emit('searchUpdated', '')
}

function isLocalityRegion(opt: filterOption): boolean {
  return (opt as { type?: string }).type === 'region'
}

function resetTempSelectedOptions() {
  if (props.filterKey === 'other') {
    return ref({
      ...props.selectedOptions,
      languages: props.selectedOptions?.languages?.slice(0) || [],
    })
  } else if (props.selectedOptions instanceof Array)
    return ref([...props.selectedOptions])
  return ref(props.selectedOptions)
}

const isFilterOpen = inject('isFilterOpen')

function onClickChip() {
  if (props.isToggle) {
    temporarySelectedOptions = resetTempSelectedOptions()
    const [toggleVal] = temporarySelectedOptions.value
    selectTemporaryOption(!toggleVal)
    return
  }

  if (!isOpen.value) {
    setIsOpen(true)
    sendFilterOpen(props.filterKey)
    isFilterOpen.value = true
  } else {
    confirmSelect()
  }
}

const activeFilterItemId = inject('activeFilterItemId')
const id = ref(`filter-item-${Math.random().toString(36).substr(2, 9)}`)
const filterItemRef = ref(null)

function setIsOpen(value: boolean) {
  activeFilterItemId.value =
    activeFilterItemId.value !== id.value ? id.value : null

  isOpen.value = value
  temporarySelectedOptions = resetTempSelectedOptions()
  if (isMobile) {
    document.body.classList.toggle('prevent-scroll')
  }
}

// Use VueUse onClickOutside for proper Vue-style click-outside handling
onClickOutside(filterItemRef, () => {
  if (isOpen.value) {
    closeFilter()
  }
})

const isChipSelected = computed<boolean>(() => {
  if (props.isToggle) {
    return !!props.selectedOptions?.at?.(0)
  }

  if (Array.isArray(props.selectedOptions)) {
    return !!props.selectedOptions.length
  }

  return !areAllValuesInObjectEmpty(props.selectedOptions)
})

const selectedOptionsCount = computed<number>(() =>
  Array.isArray(props.selectedOptions)
    ? props.selectedOptions?.length
    : countNonEmptyValuesInObject(props.selectedOptions)
)

const searchEngineLocalities = inject('searchEngineLocalities') as ComputedRef<
  SearchResponse<SearchEngineLocation>
>

const chipLabel = computed(() => {
  if (props.filterKey === 'location') {
    const filterLocation = props.selectedOptions?.at(0)
    const hit = searchEngineLocalities.value?.hits?.find((loc) =>
      localitySlugMatchesFilter(loc.slug, filterLocation)
    )
    return hit?.name ?? props.label
  }
  return props.label
})

const isLocalityOptionSelected = (optSlug: string) => {
  if (props.filterKey !== 'location') {
    return Boolean(temporarySelectedOptions.value?.includes(optSlug))
  }
  const selected = temporarySelectedOptions.value?.at(0)
  if (!selected) return false
  return localitySlugMatchesFilter(optSlug, selected) || optSlug === selected
}

const selectTempOptForSlot = (optionsObj, source?: string) => {
  if (props.filterKey === 'dates') {
    const arr = Array.isArray(optionsObj) ? optionsObj : []
    temporarySelectedOptions.value = {
      dates: arr.length ? [arr[0], arr[1] ?? arr[0]] : [],
      source,
    }
    return
  }

  if (props.filterKey === 'other' && optionsObj.languages) {
    const languages = temporarySelectedOptions.value.languages ?? []

    if (languages.includes(optionsObj.languages)) {
      temporarySelectedOptions.value = {
        ...temporarySelectedOptions.value,
        languages: languages.filter((lang) => lang !== optionsObj.languages),
      }
    } else {
      temporarySelectedOptions.value = {
        ...temporarySelectedOptions.value,
        languages: [...languages, optionsObj.languages],
      }
    }
    return
  }

  if (props.filterKey === 'other') {
    temporarySelectedOptions.value = {
      ...temporarySelectedOptions.value,
      ...optionsObj,
    }
  }
}
const selectTemporaryOption = (optionToSelect) => {
  if (!props.multiSelect) {
    const alreadySelected = isLocalityOptionSelected(optionToSelect)
    temporarySelectedOptions.value = alreadySelected ? [] : [optionToSelect]
    confirmSelect()
    return
  }
  const optionIndex = temporarySelectedOptions.value?.indexOf(optionToSelect)
  if (typeof optionIndex === 'number' && optionIndex !== -1) {
    temporarySelectedOptions.value.splice(optionIndex, 1)
  } else {
    temporarySelectedOptions.value.push(optionToSelect)
  }
}
// const clearFilters = () => {
//   temporarySelectedOptions.value = []
//   confirmSelect()
// }
const confirmSelect = () => {
  if (props.filterKey === 'other') {
    emit('filtersUpdated', toRaw(temporarySelectedOptions.value))
  } else if (props.filterKey === 'dates') {
    const datesObj = temporarySelectedOptions.value as
      | { dates?: string[]; source?: string }
      | undefined
    emit('filtersUpdated', {
      dates: datesObj?.dates?.slice(0) ?? [],
      dateSource: datesObj?.source,
    })
  } else {
    const finalSelection = !props.multiSelect
      ? nth((temporarySelectedOptions.value || []).slice(0), 0)
      : (temporarySelectedOptions.value || []).slice(0)

    emit('filtersUpdated', {
      [props.filterKey]: finalSelection,
    })
  }

  closeFilter()
}

const closeFilter = () => {
  setIsOpen(false)
  activeFilterItemId.value = null
  isFilterOpen.value = false
}

watch(
  () => activeFilterItemId.value,
  (newId) => {
    if (newId !== id.value) isOpen.value = false
  }
)

watch(isOpen, async (open) => {
  if (!open || !props.showSearch) return
  await nextTick()
  searchInputRef.value?.focus()
})
</script>

<style>
body.prevent-scroll {
  overflow: hidden;
}
</style>
<style lang="postcss" scoped>
.chip {
  @apply relative border-gray-200 hover:border-teal-900 hover:text-teal-900
  h-[34px] px-3 m-0;

  :deep(.anchor) {
    @apply ease-in-out duration-300;
  }

  &--open {
    @apply border-[#047A88] text-[#047A88] bg-teal-50;

    :deep(.anchor) {
      transform: rotate(-180deg);
    }
  }

  &--selected {
    @apply border-teal-900;
  }
}

.count {
  @apply absolute empty:hidden bg-primary inline-block text-white rounded-md
  align-middle text-center top-0 p-1 py-[2px] -mt-2 min-h-1 font-thin
  text-[0.65rem] right-2 leading-3;
}

.filter-item-wrapper {
  @apply border-gray-200 hover:border-teal-900 hover:text-teal-900
  h-[34px] md:h-[32px];
}

.filter-item-wrapper {
  @apply md:relative;
}

.filter-item {
  @apply fixed left-0 right-0 top-0 bottom-0 z-40;

  @apply md:absolute md:top-[calc(100%+8px)] md:right-auto md:bottom-auto md:z-10;

  &__content {
    @apply h-full md:h-auto w-[100vw] md:w-[480px] bg-white rounded-2xl p-2;

    .filter-item--other & {
      @apply md:w-[400px];
    }

    .filter-item--dates & {
      @apply md:w-[400px];
    }

    border-top: 1px solid #e5e7eb;
    box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.1);

    .filter-item--location &,
    .filter-item--categories & {
      @apply md:rounded-2xl md:border md:border-gray-200 md:shadow-sm md:p-4;

      box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.06);
    }
  }

  &__options {
    @apply h-[calc(100%-128px)] overflow-auto overflow-x-hidden;
    @apply md:max-h-[calc(100vh-355px)];
    @apply flex flex-col gap-1 pb-4;

    &--pill {
      @apply gap-0 pr-1;

      scrollbar-width: thin;
      scrollbar-color: #e5e7eb transparent;
    }

    .filter-item--other & {
      @apply h-[calc(100%-56px)] md:max-h-[calc(100vh-299px)];
    }

    .filter-item--location &,
    .filter-item--categories & {
      @apply md:max-h-[min(420px,calc(100vh-280px))];
    }
  }
}

.search {
  @apply relative mb-3 flex items-center gap-0 border border-gray-200 rounded-full bg-white
    pl-3 pr-1 py-0.5;
}

.search__leading-icon {
  @apply shrink-0 text-gray-400;
}

.search__clear {
  @apply shrink-0 flex items-center justify-center rounded-full p-2 min-h-[2.75rem] min-w-[2.75rem]
    text-stone-700 hover:bg-stone-100 hover:text-stone-800 transition-colors;

  :deep(svg) {
    @apply text-stone-300;
  }
}

.search-input {
  @apply h-11 min-w-0 flex-1 border-0 rounded-full pl-2 pr-2 text-base text-gray-900 bg-transparent
    outline-none ease-in-out duration-300 font-medium;

  &::placeholder {
    @apply text-stone-500 font-medium;
  }

  &:focus {
    @apply ring-0 shadow-none;
  }
}

.buttons {
  @apply max-md:fixed max-md:bottom-0 max-md:left-0 max-md:right-0 max-md:p-2 md:pt-1;
  @apply flex gap-1 bg-white rounded-b-md;

  &--hide {
    @apply md:hidden;
  }

  .button {
    flex: 1 1 100%;

    &--cancel {
      &:hover {
        background: none;
        box-shadow: none;
        text-decoration: none;
      }
    }
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
